import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "San Rafael", cities: "San Rafael, San Anselmo, Fairfax, Woodacre" },
    { zone: "Novato", cities: "Novato, Black Point, Ignacio, Atherton" },
    { zone: "Mill Valley", cities: "Mill Valley, Corte Madera, Larkspur, Kentfield" },
    { zone: "Costa de Marin", cities: "Tiburon, Belvedere, Sausalito, Stinson Beach" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Condado de Marin</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo el Condado de Marin y sus comunidades costeras.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Marin</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const MarinSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "marin", name: "Marin County", type: "region", regionSlug: "marin" }}>
      <SEO
        title="Limpieza del Hogar en el Condado de Marin, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en San Rafael, Mill Valley, Novato, Sausalito, Tiburon y todo el Condado de Marin. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar marin, servicio de limpieza condado marin, limpieza profunda san rafael, empleada doméstica mill valley, maid service marin county español, limpieza novato sausalito tiburon, servicio de limpieza marin ca"
        url="https://uplevelcleaning.com/es/marin"
        locationName="Condado de Marin"
        regionSlug="marin"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Condado de Marin", url: "/es/marin" }
        ]}
        reviews={[
          { author: "Alejandra R.", rating: 5, reviewBody: "Vivo en Mill Valley y encontrar un servicio de limpieza confiable en Marin era difícil. Uplevel Cleaning llegó puntual, fue muy profesional y dejó mi casa impecable. ¡Los recomiendo al 100%!", datePublished: "2026-03-22" },
          { author: "Bernardo M.", rating: 5, reviewBody: "Nuestra casa en Tiburon requiere mucho cuidado por su cercanía al mar. El equipo de Uplevel la trató con el respeto que merece. Resultado impecable cada vez.", datePublished: "2026-02-15" },
          { author: "Claudia N.", rating: 5, reviewBody: "Contraté el servicio quincenal en San Rafael y ha sido una excelente decisión. Siempre puntuales, siempre perfectos. Precio exacto sin sorpresas.", datePublished: "2026-01-25" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Condado de Marin" regionSlug="marin" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en Marin",
                "San Rafael, Mill Valley y Más",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Condado de Marin" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="marin" />
          <Guarantee />
          <SpanishFAQ cityName="Condado de Marin" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Condado de Marin" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default MarinSpanishPage;
